import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Eye,
  EyeOff,
  Layers,
  Loader2,
  Lock,
  RotateCcw,
  Search,
  SlidersHorizontal,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { CockpitCurtains } from "@/components/CockpitCurtains";
import { CockpitNudge } from "@/components/CockpitNudge";
import { MissingDataPanel } from "@/components/MissingDataPanel";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const blank = () => ({
  hidden_screens: [],
  shown_screens: [],
  hidden_sections: [],
  shown_sections: [],
  read_only_screens: [],
});

const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const Chip = ({ on, children, testId, onClick, tone = "primary" }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`font-display border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${
      on
        ? tone === "warn"
          ? "border-[hsl(var(--watch))] text-[hsl(var(--watch))]"
          : "border-primary text-primary"
        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);

/** One screen's row: is the tab visible, is it read-only, and which blocks inside it are off. */
const ScreenBlock = ({
  screen,
  rule,
  base,
  onChange,
  alwaysOn,
  inheritedHidden = [],
  inheritedReadOnly = [],
  inherits = false,
}) => {
  const hidden = rule.hidden_screens.includes(screen.screen);
  const shown = rule.shown_screens.includes(screen.screen);
  const inBase = base.includes(screen.screen);
  const visible = shown || (inBase && !hidden);
  const readOnly =
    rule.read_only_screens.includes(screen.screen) ||
    (inheritedReadOnly.includes(screen.screen) && !shown);
  const locked = alwaysOn.includes(screen.screen);

  const setVisible = (want) => {
    if (locked) return;
    const next = { ...rule };
    if (want) {
      next.hidden_screens = next.hidden_screens.filter((s) => s !== screen.screen);
      next.shown_screens = inBase
        ? next.shown_screens.filter((s) => s !== screen.screen)
        : toggle(next.shown_screens.filter((s) => s !== screen.screen), screen.screen);
    } else {
      next.shown_screens = next.shown_screens.filter((s) => s !== screen.screen);
      next.hidden_screens = inBase
        ? [...next.hidden_screens.filter((s) => s !== screen.screen), screen.screen]
        : next.hidden_screens;
      next.read_only_screens = next.read_only_screens.filter((s) => s !== screen.screen);
    }
    onChange(next);
  };

  const setReadOnly = (want) => {
    const next = { ...rule };
    next.read_only_screens = want
      ? [...next.read_only_screens.filter((s) => s !== screen.screen), screen.screen]
      : next.read_only_screens.filter((s) => s !== screen.screen);
    // Only a "shown" mark of their own frees an officer from what their group has locked.
    if (!want && inheritedReadOnly.includes(screen.screen)) {
      next.shown_screens = [...next.shown_screens.filter((s) => s !== screen.screen), screen.screen];
    }
    onChange(next);
  };

  const sectionOff = (key) =>
    !rule.shown_sections.includes(key) &&
    (rule.hidden_sections.includes(key) || inheritedHidden.includes(key));

  const setSection = (key, want) => {
    const next = { ...rule };
    if (want) {
      next.hidden_sections = next.hidden_sections.filter((k) => k !== key);
      next.shown_sections = inheritedHidden.includes(key)
        ? [...next.shown_sections.filter((k) => k !== key), key]
        : next.shown_sections.filter((k) => k !== key);
    } else {
      next.shown_sections = next.shown_sections.filter((k) => k !== key);
      next.hidden_sections = [...next.hidden_sections.filter((k) => k !== key), key];
    }
    onChange(next);
  };

  return (
    <div className="border-b border-border last:border-b-0" data-testid={`cockpit-screen-${screen.screen}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{screen.label}</span>
            {locked && (
              <span className="font-data border border-border px-1 text-[9px] uppercase tracking-wider text-muted-foreground">
                always on
              </span>
            )}
            {!inBase && !shown && (
              <span className="font-data text-[10px] text-muted-foreground">
                {inherits ? "off for their group" : "not part of this role"}
              </span>
            )}
            {inherits && inheritedReadOnly.includes(screen.screen) && (
              <span className="font-data text-[10px] text-[hsl(var(--watch))]">
                read-only for their group
              </span>
            )}
          </div>
          <div className="font-data mt-0.5 text-[11px] text-muted-foreground">
            {screen.sections.length} block(s) inside · {screen.screen}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-[11px]">
            {visible ? (
              <Eye className="h-3.5 w-3.5 text-primary" />
            ) : (
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <Switch
              checked={visible}
              disabled={locked}
              onCheckedChange={setVisible}
              data-testid={`cockpit-visible-${screen.screen}`}
            />
            <span className="font-data uppercase tracking-wider text-muted-foreground">
              {visible ? "visible" : "hidden"}
            </span>
          </label>
          <label className="flex items-center gap-2 text-[11px]">
            <Lock
              className={`h-3.5 w-3.5 ${readOnly ? "text-[hsl(var(--watch))]" : "text-muted-foreground"}`}
            />
            <Switch
              checked={readOnly}
              disabled={!visible}
              onCheckedChange={setReadOnly}
              data-testid={`cockpit-readonly-${screen.screen}`}
            />
            <span className="font-data uppercase tracking-wider text-muted-foreground">
              read only
            </span>
          </label>
        </div>
      </div>
      {visible && (
        <div className="flex flex-wrap gap-2 border-t border-border bg-muted/40 px-4 py-3">
          {screen.sections.map((s) => {
            const off = sectionOff(s.key);
            return (
              <Chip
                key={s.key}
                on={!off}
                testId={`cockpit-section-${s.key}`}
                onClick={() => setSection(s.key, off)}
              >
                {off ? <EyeOff className="mr-1 inline h-3 w-3" /> : null}
                {s.label}
                {inherits && inheritedHidden.includes(s.key) && !rule.shown_sections.includes(s.key)
                  ? " · group"
                  : ""}
              </Chip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Cockpit() {
  const { cockpitOwner, superAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("roles");
  const [pickedRole, setPickedRole] = useState("");
  const [pickedPerson, setPickedPerson] = useState("");
  const [draft, setDraft] = useState(blank());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/cockpit");
      setData(r.data);
      setError("");
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roles = data?.roles || [];
  const people = data?.people || [];
  const role = roles.find((r) => r.role === pickedRole);
  const person = people.find((p) => p.email === pickedPerson);

  useEffect(() => {
    if (tab === "roles" && role) setDraft({ ...blank(), ...(role.rule || {}) });
  }, [tab, role]);

  useEffect(() => {
    if (tab === "people" && person) setDraft({ ...blank(), ...(person.rule || {}) });
  }, [tab, person]);

  const base = useMemo(() => {
    if (tab === "roles") return role?.default_screens || [];
    // A person starts from what their group actually sees today, not from the portal's defaults.
    return person?.group_screens || [];
  }, [tab, role, person]);

  const save = async () => {
    setBusy(true);
    try {
      if (tab === "roles") {
        await api.put(`/cockpit/role/${pickedRole}`, draft);
        toast.success(`Saved — every ${role?.label} now sees what you set.`);
      } else {
        await api.put(`/cockpit/user/${encodeURIComponent(pickedPerson)}`, draft);
        toast.success(`Saved — ${person?.name || pickedPerson} only.`);
      }
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setLaunch = async (open) => {
    setBusy(true);
    try {
      await api.put("/cockpit/launch", { leaderboard_open: open });
      toast.success(
        open
          ? "The Leaderboard is open to everyone whose tab shows it."
          : "The Leaderboard is behind the curtain again.",
      );
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {    setBusy(true);
    try {
      if (tab === "roles") await api.delete(`/cockpit/role/${pickedRole}`);
      else await api.delete(`/cockpit/user/${encodeURIComponent(pickedPerson)}`);
      setDraft(blank());
      toast.success("Back to the portal's own standard.");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!cockpitOwner)
    return (
      <div className="p-8" data-testid="cockpit-denied">
        <h1 className="font-display text-2xl font-black">Not found</h1>
      </div>
    );

  if (!superAdmin)
    return (
      <div className="p-5 sm:p-8" data-testid="cockpit-needs-super-admin">
        <div className="label-caps text-primary">Control cockpit</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tight">
          Switch Super-Admin mode on
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          The cockpit opens in Super-Admin mode only, so nothing you do here leaves a trace. Turn it
          on from <span className="font-semibold">My profile</span> and come back.
        </p>
      </div>
    );

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        {error ? (
          <p className="text-sm text-muted-foreground" data-testid="cockpit-error">
            {error}
          </p>
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        )}
      </div>
    );

  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify({ ...blank(), ...((tab === "roles" ? role?.rule : person?.rule) || {}) });
  const filtered = people.filter(
    (p) =>
      !q ||
      (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
      p.email.includes(q.toLowerCase()),
  );

  return (
    <div className="p-5 sm:p-8" data-testid="cockpit-page">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-primary" />
        <div className="label-caps text-primary">Yours alone</div>
      </div>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
        Control cockpit
      </h1>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        What every group, and every named officer, may see and change. Switch a tab off and it leaves
        their menu and their reach — the portal refuses the data too, not just the button. Put a tab
        in read-only and they may read it but change nothing. Your own view is never touched.
      </p>

      <div className="mt-6 border border-border bg-card p-4" data-testid="cockpit-launch">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="label-caps text-primary">The Leaderboard's own curtain</div>
            <div className="mt-1 text-sm font-semibold">Leaderboard — open to everyone</div>
            <p className="mt-1 max-w-2xl text-[11px] text-muted-foreground">
              The Leaderboard is the one screen that tells the officer itself instead of refusing —
              while this is down the tab opens but the board says “coming soon”. Everything under
              Curtains below works the same way for every other tab.
            </p>
          </div>
          <label className="flex items-center gap-2 text-[11px]">
            <Switch
              checked={!!data.launch?.leaderboard_open}
              disabled={busy}
              onCheckedChange={setLaunch}
              data-testid="cockpit-launch-leaderboard"
            />
            <span className="font-data uppercase tracking-wider text-muted-foreground">
              {data.launch?.leaderboard_open ? "open to all" : "coming soon"}
            </span>
          </label>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <CockpitCurtains curtains={data.curtains} onSaved={load} />
        <CockpitNudge />
        <MissingDataPanel canRemind />
      </div>

      <div className="mt-6 flex flex-wrap gap-px bg-border" data-testid="cockpit-tabs">
        {[
          ["roles", "By group", Layers],
          ["people", "By person", User],
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            data-testid={`cockpit-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3 font-display text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${
              tab === key ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        <div className="border border-border bg-card">
          {tab === "roles" ? (
            <ul className="divide-y divide-border" data-testid="cockpit-role-list">
              {roles.map((r) => (
                <li key={r.role}>
                  <button
                    type="button"
                    data-testid={`cockpit-pick-role-${r.role}`}
                    onClick={() => setPickedRole(r.role)}
                    className={`w-full border-l-2 px-4 py-3 text-left transition-colors duration-200 hover:bg-muted ${
                      pickedRole === r.role ? "border-primary bg-muted" : "border-transparent"
                    }`}
                  >
                    <div className="text-sm font-semibold">{r.label}</div>
                    <div className="font-data text-[11px] text-muted-foreground">
                      {r.sees.screens.length} tab(s)
                      {r.sees.hidden_sections.length
                        ? ` · ${r.sees.hidden_sections.length} block(s) off`
                        : ""}
                      {r.sees.read_only_screens.length
                        ? ` · ${r.sees.read_only_screens.length} read-only`
                        : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <div className="relative border-b border-border p-3">
                <Search className="absolute left-6 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="cockpit-person-search"
                  placeholder="Find an officer…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-10 rounded-none bg-background pl-8 text-xs"
                />
              </div>
              <ul className="max-h-[520px] divide-y divide-border overflow-y-auto" data-testid="cockpit-person-list">
                {filtered.map((p) => (
                  <li key={p.email}>
                    <button
                      type="button"
                      data-testid={`cockpit-pick-person-${p.email}`}
                      onClick={() => setPickedPerson(p.email)}
                      className={`w-full border-l-2 px-4 py-3 text-left transition-colors duration-200 hover:bg-muted ${
                        pickedPerson === p.email ? "border-primary bg-muted" : "border-transparent"
                      }`}
                    >
                      <div className="text-sm font-semibold">{p.name || p.email}</div>
                      <div className="font-data text-[11px] text-muted-foreground">
                        {p.email}
                        {p.rule ? " · set by you" : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div>
          {tab === "roles" && !role ? (
            <div className="border border-border bg-card p-8" data-testid="cockpit-pick-a-group">
              <p className="text-sm text-muted-foreground">
                Pick a group on the left. What you set there holds for every officer in it, unless
                you set something for one of them by name.
              </p>
            </div>
          ) : tab === "people" && !person ? (
            <div className="border border-border bg-card p-8" data-testid="cockpit-pick-someone">
              <p className="text-sm text-muted-foreground">
                Pick an officer on the left. What you set for one person sits on top of whatever
                their group already has.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4">
                <div>
                  <div className="label-caps text-primary">
                    {tab === "roles" ? "Group" : "Officer"}
                  </div>
                  <div className="font-display text-lg font-bold">
                    {tab === "roles" ? role?.label : person?.name || person?.email}
                  </div>
                  <div className="font-data text-[11px] text-muted-foreground">
                    {tab === "roles"
                      ? `${role?.default_screens.length} tab(s) by the portal's own rule`
                      : `${person?.email} · ${roles.find((r) => r.role === person?.role)?.label}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="h-10 rounded-none"
                    disabled={busy}
                    onClick={reset}
                    data-testid="cockpit-reset"
                  >
                    <RotateCcw className="mr-2 h-3.5 w-3.5" /> Back to standard
                  </Button>
                  <Button
                    className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                    disabled={busy || !dirty}
                    onClick={save}
                    data-testid="cockpit-save"
                  >
                    {busy ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <SlidersHorizontal className="mr-2 h-4 w-4" />
                    )}
                    {dirty ? "Apply" : "Applied"}
                  </Button>
                </div>
              </div>

              <div className="mt-4 border border-border bg-card">
                {data.screens.map((screen) => (
                  <ScreenBlock
                    key={screen.screen}
                    screen={screen}
                    rule={draft}
                    base={base}
                    alwaysOn={data.always_on || []}
                    inherits={tab === "people"}
                    inheritedHidden={tab === "people" ? person?.group_hidden_sections || [] : []}
                    inheritedReadOnly={tab === "people" ? person?.group_read_only || [] : []}
                    onChange={setDraft}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
